/**
 * AdminChatbots.tsx
 * 
 * Admin page for comprehensive chatbot management and analytics.
 * Displays chatbot listings, performance metrics, moderation tools,
 * and administrative controls for platform management.
 */

import React, { useState, useEffect } from 'react'
import { 
  Bot, 
  Search, 
  Filter, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Eye, 
  Ban,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Users,
  MessageSquare,
  Star,
  Calendar,
  Download
} from 'lucide-react'
import { adminChatbotApi } from '../services/adminApi'

interface ChatbotData {
  id: string
  name: string
  creator: {
    id: string
    name: string
    email: string
  }
  status: 'active' | 'suspended' | 'pending' | 'flagged'
  category: string
  price: number
  priceType: 'free' | 'paid' | 'subscription'
  totalUsers: number
  totalMessages: number
  rating: number
  revenue: number
  createdAt: string
  lastActive: string
  description: string
  tags: string[]
}

const AdminChatbots: React.FC = () => {
  const [chatbots, setChatbots] = useState<ChatbotData[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [sortBy, setSortBy] = useState('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedChatbots, setSelectedChatbots] = useState<string[]>([])

  useEffect(() => {
    const loadChatbots = async () => {
      try {
        setLoading(true)
        
        const token = localStorage.getItem('admin_token')
        if (!token) {
          throw new Error('No admin token found')
        }

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }

        // Build query parameters
        const params = new URLSearchParams({
          sort_by: sortBy,
          sort_order: sortOrder
        })

        if (searchTerm) {
          params.append('search', searchTerm)
        }
        if (statusFilter !== 'all') {
          params.append('status', statusFilter)
        }
        if (categoryFilter !== 'all') {
          params.append('category', categoryFilter)
        }

        const response = await fetch(`http://localhost:8000/admin/chatbots?${params}`, {
          headers
        })

        if (response.ok) {
          const data = await response.json()
          setChatbots(data.chatbots || [])
        } else {
          throw new Error('Failed to load chatbots')
        }
      } catch (error) {
        console.error('Failed to load chatbots:', error)
        setChatbots([])
      } finally {
        setLoading(false)
      }
    }

    loadChatbots()
  }, [searchTerm, statusFilter, categoryFilter, sortBy, sortOrder])

  const handleBulkAction = async (action: 'suspend' | 'activate' | 'delete') => {
    if (selectedChatbots.length === 0) return

    try {
      const token = localStorage.getItem('admin_token')
      if (!token) {
        throw new Error('No admin token found')
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }

      const response = await fetch(`http://localhost:8000/admin/chatbots/bulk-action`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action,
          target_ids: selectedChatbots
        })
      })

      if (response.ok) {
        // Reload chatbots after action
        const reloadResponse = await fetch(`http://localhost:8000/admin/chatbots`, {
          headers
        })
        if (reloadResponse.ok) {
          const data = await reloadResponse.json()
          setChatbots(data.chatbots || [])
        }
        setSelectedChatbots([])
      } else {
        throw new Error(`Failed to ${action} chatbots`)
      }
    } catch (error) {
      console.error(`Failed to ${action} chatbots:`, error)
    }
  }

  const handleChatbotAction = async (chatbotId: string, action: 'suspend' | 'activate' | 'delete') => {
    try {
      const token = localStorage.getItem('admin_token')
      if (!token) {
        throw new Error('No admin token found')
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }

      // Map action to status
      const status = action === 'delete' ? 'deleted' : action === 'suspend' ? 'suspended' : 'active'

      const response = await fetch(`http://localhost:8000/admin/chatbots/${chatbotId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ status })
      })

      if (response.ok) {
        // Reload chatbots after action
        const reloadResponse = await fetch(`http://localhost:8000/admin/chatbots`, {
          headers
        })
        if (reloadResponse.ok) {
          const data = await reloadResponse.json()
          setChatbots(data.chatbots || [])
        }
      } else {
        throw new Error(`Failed to ${action} chatbot`)
      }
    } catch (error) {
      console.error(`Failed to ${action} chatbot:`, error)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'suspended': return 'bg-red-100 text-red-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'flagged': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-4 w-4" />
      case 'suspended': return <Ban className="h-4 w-4" />
      case 'pending': return <AlertCircle className="h-4 w-4" />
      case 'flagged': return <AlertCircle className="h-4 w-4" />
      default: return <AlertCircle className="h-4 w-4" />
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chatbot Management</h1>
          <p className="text-gray-600">Manage and monitor all chatbots on the platform</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors">
            <Download className="h-4 w-4 mr-2 inline" />
            Export Data
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search chatbots..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="pending">Pending</option>
              <option value="flagged">Flagged</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="business">Business</option>
              <option value="education">Education</option>
              <option value="entertainment">Entertainment</option>
              <option value="health">Health</option>
              <option value="technology">Technology</option>
            </select>
          </div>

          {/* Sort */}
          <div>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-')
                setSortBy(field)
                setSortOrder(order as 'asc' | 'desc')
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="createdAt-desc">Newest First</option>
              <option value="createdAt-asc">Oldest First</option>
              <option value="totalUsers-desc">Most Users</option>
              <option value="revenue-desc">Highest Revenue</option>
              <option value="rating-desc">Highest Rating</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedChatbots.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-blue-800">
              {selectedChatbots.length} chatbot(s) selected
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkAction('activate')}
                className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600"
              >
                Activate
              </button>
              <button
                onClick={() => handleBulkAction('suspend')}
                className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
              >
                Suspend
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chatbots Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedChatbots.length === chatbots.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedChatbots(chatbots.map(c => c.id))
                      } else {
                        setSelectedChatbots([])
                      }
                    }}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Chatbot
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creator
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {chatbots.map((chatbot) => (
                <tr key={chatbot.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedChatbots.includes(chatbot.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedChatbots([...selectedChatbots, chatbot.id])
                        } else {
                          setSelectedChatbots(selectedChatbots.filter(id => id !== chatbot.id))
                        }
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Bot className="h-5 w-5 text-primary-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{chatbot.name}</div>
                        <div className="text-sm text-gray-500">{chatbot.category}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{chatbot.creator.name}</div>
                    <div className="text-sm text-gray-500">{chatbot.creator.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(chatbot.status)}`}>
                      {getStatusIcon(chatbot.status)}
                      <span className="ml-1">{chatbot.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {chatbot.totalUsers.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(chatbot.revenue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <span className="ml-1 text-sm text-gray-900">{chatbot.rating.toFixed(1)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(chatbot.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleChatbotAction(chatbot.id, chatbot.status === 'active' ? 'suspend' : 'activate')}
                        className={`px-2 py-1 text-xs rounded ${
                          chatbot.status === 'active' 
                            ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {chatbot.status === 'active' ? 'Suspend' : 'Activate'}
                      </button>
                      <button className="text-gray-400 hover:text-gray-600">
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Bot className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Chatbots</p>
              <p className="text-2xl font-bold text-gray-900">{chatbots.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">
                {chatbots.filter(c => c.status === 'active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Flagged</p>
              <p className="text-2xl font-bold text-gray-900">
                {chatbots.filter(c => c.status === 'flagged').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(chatbots.reduce((sum, c) => sum + c.revenue, 0))}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminChatbots 